import React from "react";

import { UseFormReturn } from "react-hook-form";
import * as Yup from "yup";

import {
    CustomerFormProps,
    CustomerFormState
} from "./models";

import {
    CheckVatResponseObject,
    Customer,
    MainConstants
} from "../../../pages/models";

import {
    Button,
    Grid,
    InputAdornment,
    Stack
} from "@mui/material";

import {
    Loading,
    MatesSelect,
    MatesSwitch,
    MatesTextField,
    withUseFormHook,
} from "@sinossi/mates-react-library";

import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import PhoneIcon from "@mui/icons-material/Phone";

import {
    t,
    Trans
} from "@lingui/macro";

import {
    withSnackbar,
    WithSnackbarProps
} from "notistack";
import { CustomersService } from "../../../services/customers.service";
import InfoDialog from "../../shared/info-dialog/info-dialog";
import "./customer-form.css";

const phoneRegExp = /^\+?\d{7,}$/;

const CustomerSchema = Yup.object().shape({

    fiscalCode: Yup.string().required(t({
        id: "cf-cs-codfiscale-required-msg",
        message: "Il codice fiscale è obbligatorio"
    })),
    email: Yup.string().email(t({
        id: "cf-cs-email-valid-msg",
        message: "Inserire un indirizzo e-mail valido"
    })).required(t({
        id: "cf-cs-email-required-msg",
        message: "L'indirizzo e-mail è obbligatorio"
    })), // denomination: Yup.string().required(t({
    // 	id: "cf-cs-denomination-required-msg",
    // 	message: "La denominazione è obbligatoria"
    // })),
    legalNature: Yup.string().required(t({
        id: "cf-cs-legal-nature-required-msg",
        message: "La natura giuridica è obbligatoria"
    })),
    mainActivity: Yup.string().required(t({
        id: "cf-cs-main-activity-required-msg",
        message: "La prevalente attività è obbligatoria"
    })),
    geoArea: Yup.string().required(t({
        id: "cf-cs-geo-area-required-msg",
        message: "L'area di residenza è obbligatoria"
    })),
    contactPhone: Yup.string()
        .nullable()
        .matches(phoneRegExp, {
            message: t({
                id: "cf-cs-phone-number-valid-msg",
                message: "Inserire un numero di telefono valido"
            }),
            excludeEmptyString: true // Esclude la stringa vuota dalla validazione regex
        }),
    contactName: Yup.string().required(t({
        id: "cf-cs-name-required-msg",
        message: "Il nome di contatto è obbligatorio"
    })),
    contactSurname: Yup.string().required(t({
        id: "cf-cs-surname-required-msg",
        message: "Il cognome di contatto è obbligatorio"
    })),

});

class CustomerForm extends React.Component<CustomerFormProps & UseFormReturn & WithSnackbarProps, CustomerFormState> {

    private viesTimeout: any = null;

    constructor(props: any) {
        super(props);

        this.state = {
            insertedCustomer: null,
            isLoading: true,
            isMainActivityInfoModalOpen: false,
            isContactDataChange: false
        };
    }

    /**
     * Builds a new active customer with empty server-controlled termination audit metadata.
     */
    private submitForm = (values: any) => {

        let pfOrDiCheck = values.legalNature === "PF" || values.legalNature === "DI" || values.legalNature === "PP";

        let customer: Customer = {
            additionalInfo: {
                collaborative: values.collaborative != null ? values.collaborative : false,
                mainActivity: values.mainActivity,
                ateco2007: null,
                constitutionDate: null,
                birthDate: null,
                address: null,
                businessType: null,
                authCode: null,
                advDuration: null,
                requiredDocuments: null
            },
            deleted: null,
            deletedAt: null,
            deletedBy: null,
            efestoProcessId: null,
            fiscalCode: values.fiscalCode,
            id: null,
            name: pfOrDiCheck ? values.name : null,
            office: null,
            ownerList: null,
            pec: null,
            status: null,
            surname: pfOrDiCheck ? values.surname : null,
            vatCode: null,
            verificationExpireDate: null,
            verificationType: null,
            email: values.email,
            denomination: !pfOrDiCheck ? values.denomination : null,
            contactName: values.contactName,
            contactSurname: values.contactSurname,
            contactPhone: values.contactPhone,
            legalNature: values.legalNature,
            geoArea: values.geoArea,
            managementCode: values.managementCode,
            worker: null,
            documentList: null
        };

        this.setState(() => ({
            insertedCustomer: customer,
            isLoading: true
        }));

        CustomersService.checkCustomerExists(customer, this.checkCustomerExistsCallback);
    };

    componentDidMount() {

        this.setState(() => ({ isLoading: true }));

        if (this.props.customer !== null) {
            this.props.reset(this.props.customer);
        }

        this.setState(() => ({ isLoading: false }));
    }

    private checkCustomerExistsCallback = (response: any) => {

        this.setState(() => ({ isLoading: false }));

        console.log(response);

        if (response.status !== 200) {
            this.props.enqueueSnackbar(t({
                id: "cf-error-customer-exists-error",
                message: "Il cliente inserito esiste già"
            }), {
                variant: "error",
            });
        } else {
            this.props.onCustomerSubmit(this.state.insertedCustomer);
        }
    };

    setMainActivityInfoModalVisibility = () => {
        this.setState(() => ({ isMainActivityInfoModalOpen: !this.state.isMainActivityInfoModalOpen }));
    };

    customIsValid = () => {

        let pfOrDiCheck = this.props.getValues().legalNature === "PF" || this.props.getValues().legalNature === "DI" || this.props.getValues().legalNature === "PP";

        if (pfOrDiCheck) {

            let name = this.props.getValues("name");
            if (name === undefined || name === "") {
                return false;
            }

            let surname = this.props.getValues("surname");
            if (surname === undefined || surname === "") {
                return false;
            }
        }

        if (!pfOrDiCheck) {
            let denomination = this.props.getValues("denomination");
            if (denomination === undefined || denomination === "") {
                return false;
            }
        }

        return true;
    };

    updateDataCallback = (response: any) => {

        if (!response.hasError()) {
            this.setState(() => ({ isContactDataChange: false }));
        } else {
            this.props.enqueueSnackbar(t({
                id: "cf-error-update-contact-data",
                message: "Errore nel salvataggio dei dati"
            }), {
                variant: "error",
            });
        }
    }


    private callVies = (vatCode: string) => {

        if (this.viesTimeout !== null) {
            clearTimeout(this.viesTimeout)
        }
        this.viesTimeout = setTimeout(() => {
            if (/^\d{11}$/.test(vatCode)) {
                vatCode = 'IT' + vatCode
            }
            let isValid = this.validateItVatCode(vatCode)

            if (isValid) {
                this.setState(() => ({ isLoading: true }))
                CustomersService.checkViesVatCode(vatCode.substr(2), this.viesCallback)
            }

        }, 500)

        return vatCode;
    }

    private readonly validateItVatCode = (vatCode: string) => {

        if (vatCode.startsWith("IT")) {
            let vatNumber = vatCode.substr(2)
            let v = Array(...Array(11))
            const sum: number = v
                .map((_, it: number) => CustomerForm._getDigit(vatNumber.charAt(it), it))
                .reduce((a: number, b: number) => a + b)

            return sum % 10 === 0
        }
        return false
    }

    private static readonly _getDigit = (c: string, i: number) => {
        let digit = c.charCodeAt(0) - "0".charCodeAt(0)
        if (i % 2 === 0) {
            return digit
        }

        digit *= 2
        if (digit > 9) {
            digit -= 9
        }

        return digit
    }


    private readonly viesCallback = (data: CheckVatResponseObject) => {

        if (data?.data?.valid && data?.data?.name?.value) {
            this.props.setValue("denomination", data.data.name.value)
        }
        this.setState(() => ({ isLoading: false }))
    }

    private readonly saveChanges = () => {

        let request = {
            name: this.props.getValues().contactName,
            surname: this.props.getValues().contactSurname,
            phone: this.props.getValues().contactPhone,
            email: this.props.getValues().email,
            managementCode: this.props.getValues().managementCode
        };

        CustomersService.updateContactData(this.props.customer?.id, request, this.updateDataCallback);
    }

    render() {

        let pfOrDiCheck = this.props.getValues().legalNature === "PF" || this.props.getValues().legalNature === "DI" || this.props.getValues().legalNature === "PP";

        let currentLanguage = window.localStorage.getItem("selected_language") || "IT";

        return (<>
            {this.state.isMainActivityInfoModalOpen && <InfoDialog simpleText={t({
                id: "cf-main-activity-info-text",
                message: "Lorem ipsum dolor sit amet"
            })} title={t({
                id: "cf-main-activity-info-title",
                message: "Prevalente attività"
            })}
                closingButtonAction={this.setMainActivityInfoModalVisibility} />}
            {this.state.isLoading && <Loading show />}
            {!this.state.isLoading && <form onSubmit={this.props.handleSubmit(this.submitForm)}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <MatesSelect
                            variant={"outlined"}
                            name="legalNature"
                            label={t({
                                id: "cf-legal-nature",
                                message: "Natura Giuridica"
                            })}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={this.props.readOnly}>

                            {MainConstants.CUSTOMER_LEGAL_NATURE_LIST.map(
                                (item) => (<option value={item.key}
                                    key={item.key}>{currentLanguage === "IT" ? item.label : item.labelEn}</option>))}

                        </MatesSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <MatesTextField
                            variant={"outlined"}
                            name="fiscalCode"
                            label={t({
                                id: "cf-fiscal-code",
                                message: "Codice fiscale"
                            })}
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState}
                            disabled={this.props.readOnly}
                            onChange={this.callVies}
                        />
                    </Grid>
                    {!pfOrDiCheck && <Grid item xs={12}>
                        <MatesTextField
                            variant={"outlined"}
                            name="denomination"
                            label={t({
                                id: "cf-denomination",
                                message: "Denominazione"
                            })}
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState}
                            disabled={this.props.readOnly} />
                    </Grid>}
                    {pfOrDiCheck && <>
                        <Grid item xs={6}>
                            <MatesTextField
                                variant={"outlined"}
                                name="name"
                                label={t({
                                    id: "cf-name",
                                    message: "Nome"
                                })}
                                control={this.props.control}
                                trigger={this.props.trigger}
                                formState={this.props.formState}
                                disabled={this.props.readOnly} />
                        </Grid>
                        <Grid item xs={6}>
                            <MatesTextField
                                variant={"outlined"}
                                name="surname"
                                label={t({
                                    id: "cf-surname",
                                    message: "Cognome"
                                })}
                                control={this.props.control}
                                trigger={this.props.trigger}
                                formState={this.props.formState}
                                disabled={this.props.readOnly} />
                        </Grid>
                    </>}
                    <Grid item xs={6}>
                        <MatesSelect
                            name="geoArea"
                            variant={"outlined"}
                            label={t({
                                id: "cf-geo-area",
                                message: "Area Geografica di residenza"
                            })}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={this.props.readOnly}>

                            {MainConstants.GEO_AREA.map((item) => (<option value={item.key}
                                key={item.key}>{currentLanguage === "IT" ? item.label : item.labelEn}</option>))}
                        </MatesSelect>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack direction={"row"} spacing={1} style={{
                            display: "inline-flex",
                            alignItems: "center",
                            width: "100%"
                        }}>
                            <MatesSelect
                                name={!this.props.readOnly ? "mainActivity" : "additionalInfo.mainActivity"}
                                variant={"outlined"}
                                label={t({
                                    id: "cf-main-activity",
                                    message: "Prevalente attività"
                                })}
                                control={this.props.control}
                                formState={this.props.formState}
                                trigger={this.props.trigger}
                                disabled={this.props.readOnly}>
                                {MainConstants.CUSTOMER_MAIN_ACTIVITY.map(
                                    (item) => (<option value={item.key}
                                        key={item.key}>{currentLanguage === "IT" ? item.label : item.labelEn}</option>))}
                            </MatesSelect>
                            <InfoIcon
                                sx={{
                                    color: "blue",
                                    cursor: "pointer"
                                }}
                                onClick={this.setMainActivityInfoModalVisibility}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <MatesSwitch
                            name={!this.props.readOnly ? "collaborative" : "additionalInfo.collaborative"}
                            label={t({
                                id: "cf-collaborative",
                                message: "Il cliente si è dimostrato collaborativo e trasparente"
                            })}
                            control={this.props.control}
                            formState={this.props.formState}
                            disabled={this.props.readOnly}
                            trigger={this.props.trigger} />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="contactName"
                            variant={"outlined"}
                            label={t({
                                id: "cf-contact-name",
                                message: "Nome di contatto"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="contactSurname"
                            variant={"outlined"}
                            label={t({
                                id: "cf-contact-surname",
                                message: "Cognome di contatto"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="contactPhone"
                            variant={"outlined"}
                            label={t({
                                id: "cf-phone",
                                message: "Cellulare"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                            endAdornment={<InputAdornment position="end">
                                <PhoneIcon />
                            </InputAdornment>}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="email"
                            variant={"outlined"}
                            label={t({
                                id: "cf-email",
                                message: "Indirizzo e-mail"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                            endAdornment={<InputAdornment position="end">
                                <EmailIcon />
                            </InputAdornment>}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="managementCode"
                            variant={"outlined"}
                            label={t({
                                id: "cf-management-code",
                                message: "Codice Gestionale"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e;
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                        />
                    </Grid>
                    {!this.props.readOnly && <Grid item xs={12} textAlign="right">
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={this.props.formState.isSubmitting || !this.props.formState.isValid || this.state.isLoading || !this.customIsValid()}
                            type="submit">
                            <Trans id={"cf-go-ahead"}>
                                Avanti
                            </Trans>
                        </Button>
                    </Grid>}
                    {this.props.readOnly && <Grid item xs={12} textAlign="right">
                        <Button
                            onClick={this.saveChanges}
                            color="primary"
                            variant="contained"
                            disabled={!this.state.isContactDataChange}>
                            <Trans id={"cf-save"}>Salva</Trans>
                        </Button>
                    </Grid>}
                </Grid>
            </form>}
        </>);
    }
}

const componentWithForm = withUseFormHook<{}>(CustomerForm, CustomerSchema, {
    mainActivity: "NORMAL",
    geoArea: "ITALY"
});

export default withSnackbar(componentWithForm);
